import React from "react";
import { FlexGrid, FlexGridProps } from "@clearabee/ui-library-native";
import { Input } from "components/Core/Input/Input";
import { useFormContext } from "lib/hooks/useFormContext";
import { useTranslation } from "react-i18next";
import { FieldComponentProps } from "lib/formTypes";

interface AddressFieldProps
  extends Pick<
      FieldComponentProps,
      "labelStyles" | "errorBackground" | "errorStyles" | "invertErrorColors"
    >,
    Pick<FlexGridProps, "cellSpacing"> {
  prefix: string;
  disablePostcode?: boolean;
}

export const AddressField = ({
  prefix: initialPrefix,
  disablePostcode = false,
  cellSpacing,
  ...fieldProps
}: AddressFieldProps): React.ReactElement => {
  const [translate] = useTranslation();
  const { Field } = useFormContext();
  const prefix = initialPrefix ? `${initialPrefix}.` : "";

  return (
    <FlexGrid cellSpacing={cellSpacing} rowSpacing={null}>
      <FlexGrid.Cell base="100%" xsmall="50%">
        <Field
          name={`${prefix}line1`}
          label={translate("common.addressLine1")}
          required
          {...fieldProps}
        >
          {({ field }) => (
            <Input.Text
              {...field}
              placeholder={translate("common.form.placeholder.addressLine1")}
            />
          )}
        </Field>
      </FlexGrid.Cell>
      <FlexGrid.Cell base="100%" xsmall="50%">
        <Field
          name={`${prefix}line2`}
          label={translate("common.addressLine2")}
          {...fieldProps}
        >
          {({ field }) => (
            <Input.Text
              {...field}
              placeholder={translate("common.form.placeholder.addressLine2")}
            />
          )}
        </Field>
      </FlexGrid.Cell>
      <FlexGrid.Cell base="100%" xsmall="50%">
        <Field
          name={`${prefix}city`}
          label={translate("common.city")}
          required
          {...fieldProps}
        >
          {({ field }) => (
            <Input.Text
              {...field}
              placeholder={translate("common.form.placeholder.city")}
            />
          )}
        </Field>
      </FlexGrid.Cell>
      <FlexGrid.Cell base="100%" xsmall="50%">
        <Field
          name={`${prefix}county`}
          label={translate("common.county")}
          {...fieldProps}
        >
          {({ field }) => (
            <Input.Text
              {...field}
              placeholder={translate("common.form.placeholder.county")}
            />
          )}
        </Field>
      </FlexGrid.Cell>
      <FlexGrid.Cell base="100%" xsmall="50%">
        <Field
          name={`${prefix}postcode`}
          label={translate("common.postcode")}
          required
          {...fieldProps}
        >
          {({ field }) => (
            <Input.Text
              {...field}
              disabled={disablePostcode}
              placeholder={translate("common.form.placeholder.postcode")}
            />
          )}
        </Field>
      </FlexGrid.Cell>
    </FlexGrid>
  );
};
